import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button, Calendar, Icon, Input } from "@elaniintc/goes-design-system";
import { useState, useEffect, useMemo } from "react";
import {
  type MaskitoDateParams,
  maskitoDateOptionsGenerator,
  maskitoStringifyDate,
  maskitoParseDate,
  maskitoTimeOptionsGenerator,
} from "@maskito/kit";
import { useMaskito } from "@maskito/react";
import dayjs from "dayjs";
import { cn } from "@/utils/theme";
import { localDateToIso } from "@/utils/dates";

const params: MaskitoDateParams = {
  mode: "dd/mm/yyyy",
  separator: "/",
};
const options = maskitoDateOptionsGenerator(params);

function clampDate(d: Date, minDate?: Date, maxDate?: Date) {
  if (minDate && d < minDate) return minDate;
  if (maxDate && d > maxDate) return maxDate;
  return d;
}

function fmt(date?: Date) {
  return date
    ? maskitoStringifyDate(date, { separator: "/", mode: "dd/mm/yyyy" })
    : "";
}

// Función para normalizar el valor de horas
const normalizeHoursValue = (value: string, format24: boolean) => {
  if (!value) return "";

  if (format24) {
    // Si es solo números (ej: "1", "23"), convertir a formato HH:MM (ej: "1:00", "23:00")
    if (/^[0-9]{1,2}$/.test(value)) {
      return `${value}:00`;
    }
    // Si ya está en formato HH:MM, mantenerlo
    return value;
  } else {
    // Para formato 12 horas
    if (/^[0-9]{1,2}$/.test(value)) {
      return `${value}:00 AM`;
    }
    return value;
  }
};

interface DateInputProps {
  onChange: (date: string) => void;
  value?: string;
  placeholder?: string;
  group?:
    | "down"
    | "down-left"
    | "down-right"
    | "left"
    | "right"
    | "up"
    | "up-left"
    | "up-right"
    | "single"
    | "middle"
    | "middle-right"
    | "middle-left"
    | "middle-up";
  className?: string;
  wrapperClassName?: string;
  minDate?: Date | string | null;
  maxDate?: Date | string | null;
  includeTime?: boolean;
  format24?: boolean;
  captureCurrentTime?: boolean;
}

export const DateInput = ({
  onChange: onChangeDate,
  value,
  group,
  placeholder = "Seleccionar fecha",
  className,
  wrapperClassName,
  maxDate: maxDateProp,
  minDate: minDateProp,
  format24 = true,
  includeTime = false,
  captureCurrentTime = false,
}: DateInputProps) => {
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState<Date | undefined>(undefined);
  const [dateValue, setDateValue] = useState<string>("");
  const [timeValue, setTimeValue] = useState<string>("");

  // Convert minDate and maxDate to Date objects if they are strings
  const minDate = useMemo(() => {
    if (!minDateProp) return undefined;

    const d =
      typeof minDateProp === "string"
        ? dayjs(minDateProp, "DD/MM/YYYY", true).toDate()
        : new Date(minDateProp);

    if (isNaN(d.getTime())) return undefined;
    d.setHours(0, 0, 0, 0);
    return d;
  }, [minDateProp]);

  const maxDate = useMemo(() => {
    if (!maxDateProp) return undefined;

    const d =
      typeof maxDateProp === "string"
        ? dayjs(maxDateProp, "DD/MM/YYYY", true).toDate()
        : new Date(maxDateProp);

    if (isNaN(d.getTime())) return undefined;
    d.setHours(23, 59, 59, 999);
    return d;
  }, [maxDateProp]);

  const maskedInputRef = useMaskito({ options });

  const timeMaskOptions = maskitoTimeOptionsGenerator(
    format24
      ? {
          mode: "HH:MM",
          timeSegmentMinValues: { hours: 0, minutes: 0 },
          timeSegmentMaxValues: { hours: 23, minutes: 59 },
        }
      : {
          mode: "HH:MM AA",
          timeSegmentMinValues: { hours: 1, minutes: 0 },
          timeSegmentMaxValues: { hours: 12, minutes: 59 },
        },
  );

  const timeMask = useMaskito({ options: timeMaskOptions });

  const updateElement = (_key: "hours", newValue: string) => {
    setTimeValue(newValue);
    if (date) {
      let hours = 0;
      let minutes = 0;

      if (format24) {
        const [h = "0", m = "0"] = newValue.split(":");
        hours = parseInt(h, 10) || 0;
        minutes = parseInt(m, 10) || 0;
      } else {
        // Parse formato 12 horas
        const match = newValue.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)?$/i);
        if (match) {
          let h = parseInt(match[1], 10) || 0;
          const m = parseInt(match[2], 10) || 0;
          const period = match[3]?.toUpperCase() || "AM";

          if (period === "PM" && h !== 12) h += 12;
          if (period === "AM" && h === 12) h = 0;

          hours = h;
          minutes = m;
        }
      }

      const updatedDate = new Date(date);
      updatedDate.setHours(hours);
      updatedDate.setMinutes(minutes);
      updatedDate.setSeconds(0);
      updatedDate.setMilliseconds(0);
      onChangeDate(updatedDate.toISOString());
    }
  };

  useEffect(() => {
    if (value) {
      // Try parsing as ISO first, then as DD/MM/YYYY
      let parsed = dayjs(value).toDate();

      // If ISO parsing failed, try DD/MM/YYYY format
      if (isNaN(parsed.getTime())) {
        parsed = dayjs(value, "DD/MM/YYYY", true).toDate();
      }

      if (!isNaN(parsed.getTime())) {
        const safe = clampDate(parsed, minDate, maxDate);
        setDate(safe);
        setDateValue(fmt(safe));

        // Extract time if includeTime is enabled
        if (includeTime) {
          const hours24 = safe.getHours();
          const minutes = String(safe.getMinutes()).padStart(2, "0");

          if (format24) {
            const hours = String(hours24).padStart(2, "0");
            setTimeValue(`${hours}:${minutes}`);
          } else {
            const hours12 = hours24 % 12 || 12;
            const period = hours24 >= 12 ? "PM" : "AM";
            setTimeValue(
              `${String(hours12).padStart(2, "0")}:${minutes} ${period}`,
            );
          }
        }

        if (safe.getTime() !== parsed.getTime()) {
          onChangeDate(safe.toISOString());
        }
        return;
      }
    }
    setDate(undefined);
    setDateValue("");
    setTimeValue("");
  }, [value, includeTime]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className={cn("flex w-full", wrapperClassName)}>
      <div className="relative w-full">
        <Input
          group={
            includeTime ? (group !== "right" ? "left" : "middle-up") : group
          }
          ref={maskedInputRef}
          className={cn(
            "w-full border border-neutral bg-neutral-object-accent-silent pr-10",
            className,
          )}
          value={dateValue}
          placeholder={placeholder}
          onBlur={() => {
            const parsed = maskitoParseDate(dateValue, params);

            if (!parsed) {
              if (date) {
                setDateValue(fmt(date));
              } else {
                setDateValue("");
                onChangeDate("");
              }
              return;
            }

            const safe = clampDate(parsed, minDate, maxDate);

            // If includeTime, apply the time from timeValue
            if (includeTime && timeValue) {
              let hours = 0;
              let minutes = 0;

              if (format24) {
                const [h = "0", m = "0"] = timeValue.split(":");
                hours = parseInt(h, 10) || 0;
                minutes = parseInt(m, 10) || 0;
              } else {
                // Parse formato 12 horas con AM/PM
                const match = timeValue.match(
                  /^(\d{1,2}):(\d{2})\s*(AM|PM)?$/i,
                );
                if (match) {
                  let h = parseInt(match[1], 10) || 0;
                  const m = parseInt(match[2], 10) || 0;
                  const period = match[3]?.toUpperCase() || "AM";

                  if (period === "PM" && h !== 12) h += 12;
                  if (period === "AM" && h === 12) h = 0;

                  hours = h;
                  minutes = m;
                }
              }

              safe.setHours(hours);
              safe.setMinutes(minutes);
              safe.setSeconds(0);
              safe.setMilliseconds(0);
            }

            setDate(safe);
            setDateValue(fmt(safe));

            // Apply captureCurrentTime only when calling onChange
            const dateToSend = new Date(safe);
            if (captureCurrentTime) {
              const now = new Date();
              dateToSend.setHours(now.getHours());
              dateToSend.setMinutes(now.getMinutes());
              dateToSend.setSeconds(now.getSeconds());
              dateToSend.setMilliseconds(0);
              const localIso = localDateToIso(dateToSend);
              onChangeDate(localIso || "");
            } else {
              onChangeDate(dateToSend.toISOString());
            }
          }}
          onChange={(e) => {
            const raw = e.target.value;
            const parsed = maskitoParseDate(raw, params);

            if (!parsed && raw.length === 0) {
              setDate(undefined);
              setDateValue("");
              onChangeDate("");
              return;
            }

            if (!parsed) {
              setDateValue(raw);
              return;
            }

            // If includeTime, apply the time from timeValue
            if (includeTime && timeValue) {
              let hours = 0;
              let minutes = 0;

              if (format24) {
                const [h = "0", m = "0"] = timeValue.split(":");
                hours = parseInt(h, 10) || 0;
                minutes = parseInt(m, 10) || 0;
              } else {
                // Parse formato 12 horas con AM/PM
                const match = timeValue.match(
                  /^(\d{1,2}):(\d{2})\s*(AM|PM)?$/i,
                );
                if (match) {
                  let h = parseInt(match[1], 10) || 0;
                  const m = parseInt(match[2], 10) || 0;
                  const period = match[3]?.toUpperCase() || "AM";

                  if (period === "PM" && h !== 12) h += 12;
                  if (period === "AM" && h === 12) h = 0;

                  hours = h;
                  minutes = m;
                }
              }

              parsed.setHours(hours);
              parsed.setMinutes(minutes);
              parsed.setSeconds(0);
              parsed.setMilliseconds(0);
            }

            // Validate against minDate and maxDate
            if (
              (minDate && parsed < minDate) ||
              (maxDate && parsed > maxDate)
            ) {
              setDateValue(raw);
              return;
            }

            setDate(parsed);
            setDateValue(raw);

            // Apply captureCurrentTime only when calling onChange
            const dateToSend = new Date(parsed);
            if (captureCurrentTime) {
              const now = new Date();
              dateToSend.setHours(now.getHours());
              dateToSend.setMinutes(now.getMinutes());
              dateToSend.setSeconds(now.getSeconds());
              dateToSend.setMilliseconds(0);
              const localIso = localDateToIso(dateToSend);
              onChangeDate(localIso || "");
            } else {
              onChangeDate(dateToSend.toISOString());
            }
          }}
        />
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              aria-label="Seleccionar fecha"
              className="top-1.5 right-2 absolute"
            >
              <Icon type="far" name="calendar" />
            </Button>
          </PopoverTrigger>

          <PopoverContent
            className="w-full overflow-hidden p-0 bg-neutral-object border-neutral"
            align="end"
            data-testid="date-input-popover"
          >
            <Calendar
              mode="single"
              selected={date}
              captionLayout="dropdown"
              defaultMonth={
                date ? clampDate(date, minDate, maxDate) : new Date()
              }
              disabled={(date) => {
                const normalizedDate = new Date(date);
                normalizedDate.setHours(0, 0, 0, 0);

                if (minDate) {
                  const normalizedMin = new Date(minDate);
                  normalizedMin.setHours(0, 0, 0, 0);
                  if (normalizedDate < normalizedMin) return true;
                }

                if (maxDate) {
                  const normalizedMax = new Date(maxDate);
                  normalizedMax.setHours(0, 0, 0, 0);
                  if (normalizedDate > normalizedMax) return true;
                }

                return false;
              }}
              className="bg-transparent w-full"
              onSelect={(d?: Date) => {
                if (!d) return;
                const safe = clampDate(d, minDate, maxDate);

                // Mantener la hora actual si ya existe una fecha seleccionada
                if (date && includeTime) {
                  safe.setHours(date.getHours());
                  safe.setMinutes(date.getMinutes());
                  safe.setSeconds(0);
                  safe.setMilliseconds(0);
                }

                setDate(safe);
                setDateValue(fmt(safe));

                // Apply captureCurrentTime only when calling onChange
                const dateToSend = new Date(safe);
                if (captureCurrentTime) {
                  const now = new Date();
                  dateToSend.setHours(now.getHours());
                  dateToSend.setMinutes(now.getMinutes());
                  dateToSend.setSeconds(now.getSeconds());
                  dateToSend.setMilliseconds(0);
                  const localIso = localDateToIso(dateToSend);
                  onChangeDate(localIso || "");
                } else {
                  onChangeDate(dateToSend.toISOString());
                }
                setOpen(false);
              }}
            />
          </PopoverContent>
        </Popover>
      </div>
      {includeTime && (
        <Input
          group={
            includeTime ? (group !== "left" ? "right" : "middle-up") : group
          }
          className={group === "left" || group === "right" ? "w-1/2" : "w-1/3"}
          placeholder="Elige una hora"
          value={timeValue}
          ref={timeMask}
          onChange={(e) => {
            updateElement("hours", e.target.value);
          }}
          onBlur={(e) => {
            let normalizedValue = normalizeHoursValue(e.target.value, format24);

            // Si es formato 12h y el valor no tiene AM/PM, agregar AM automáticamente
            if (
              !format24 &&
              normalizedValue &&
              !/AM|PM/i.test(normalizedValue)
            ) {
              if (/^\d{1,2}:\d{2}$/.test(normalizedValue)) {
                normalizedValue = `${normalizedValue} AM`;
              }
            }

            if (normalizedValue !== e.target.value) {
              updateElement("hours", normalizedValue);
            }
          }}
        />
      )}
    </div>
  );
};
